import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, EyeOffIcon, LoaderCircleIcon, LockIcon, TriangleAlertIcon } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import { campaigns, planMeta } from '../data/plan';
const PASSWORD = 'kingkong';
interface LoginProps {
  onSuccess: () => void;
}
export function Login({
  onSuccess
}: LoginProps) {
  const {
    theme
  } = useTheme();
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setError('');
    setPending(true);
    window.setTimeout(() => {
      if (password === PASSWORD) {
        onSuccess();
      } else {
        setPending(false);
        setError('That password doesn’t match. Check with the content lead.');
      }
    }, 450);
  }
  return <div className="flex min-h-full w-full flex-col bg-ink-950 font-sans lg:flex-row" data-id="element-171" data-mp="2rey7isgtm2c7">
      <aside className="relative hidden flex-col justify-between border-r border-ink-700 bg-ink-900 px-10 py-10 lg:flex lg:w-[46%]" data-id="element-172" data-mp="2mjsmdvv8srp6">
        <span className="pointer-events-none absolute inset-0 grain opacity-60" aria-hidden="true" data-id="element-173" data-mp="2wg5b0fbxkfd2" />

        <img src="https://cdn.magicpatterns.com/uploads/e7dX1XaU16LT62JhgqNWnJ/Schermafbeelding_2026-07-29_174752.png" alt="BitterBrains" className={`relative h-8 w-auto object-contain ${theme === 'dark' ? 'rounded-md bg-white px-2 py-1' : ''}`} data-id="element-174" data-mp="3a7a6d589mlhl" />

        <div className="relative max-w-md" data-id="element-175" data-mp="3k3dgax9czof3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-campaign" data-id="element-176" data-mp="3f7ohwx92sdkd">{planMeta.window}</p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-chalk" data-id="element-177" data-mp="1psi0pazrb92t">
            The social content calendar, as one continuous plan.
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-mute" data-id="element-178" data-mp="2ah127wu8t56q">
            {planMeta.anchors} anchor slots and {planMeta.slate} always-on slots across{' '}
            {campaigns.length} campaigns and {planMeta.weeks} weeks — hero periods, pillar balance, batching and
            measurement in one place.
          </p>
        </div>

        <dl className="relative grid grid-cols-3 gap-4 border-t border-ink-700 pt-6" data-id="element-179" data-mp="2ohbu937agpth">
          {[{
          label: 'Planned slots',
          value: planMeta.total
        }, {
          label: 'Campaigns',
          value: campaigns.length
        }, {
          label: 'Weeks',
          value: planMeta.weeks
        }].map(stat => <div key={stat.label} data-id="element-180" data-mp="328gykillq6a5">
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint" data-id="element-181" data-mp="3770kwk43xk31">{stat.label}</dt>
              <dd className="mt-1 font-display text-2xl text-chalk" data-id="element-182" data-mp="3c63cb1r5zrsj">{stat.value}</dd>
            </div>)}
        </dl>
      </aside>

      <main className="flex flex-1 flex-col px-5 py-6 sm:px-10" data-id="element-183" data-mp="3h5zjtaz2m1cu">
        <div className="flex items-center justify-between gap-4" data-id="element-184" data-mp="15hl0i88gmey5">
          <img src="https://cdn.magicpatterns.com/uploads/e7dX1XaU16LT62JhgqNWnJ/Schermafbeelding_2026-07-29_174752.png" alt="BitterBrains" className={`h-7 w-auto object-contain lg:hidden ${theme === 'dark' ? 'rounded-md bg-white px-2 py-1' : ''}`} data-id="element-185" data-mp="15frqciyn1cle" />
          <span className="hidden lg:block" data-id="element-186" data-mp="qmojb5ioah8x" />
          <ThemeToggle data-id="element-187" data-mp="1o8luehuq3kau" />
        </div>

        <div className="flex flex-1 items-center justify-center py-10" data-id="element-188" data-mp="1o7kikb5vuts2">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-full max-w-sm" data-id="element-189" data-mp="1jaam4bk2thuq">
            <span className="inline-flex items-center gap-2 rounded-full border border-ink-700 bg-ink-850 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-mute" data-id="element-190" data-mp="19j67xe28610y">
              <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-191" data-mp="227btg313ru1u" />
              Internal tool
            </span>

            <h2 className="mt-5 font-display text-3xl leading-tight text-chalk" data-id="element-192" data-mp="1mosmlccafjy1">Sign in to Content Ops</h2>
            <p className="mt-2 text-sm leading-relaxed text-mute" data-id="element-193" data-mp="1rqhwuprortl2">
              This dashboard is shared with the team. Enter the workspace password to continue.
            </p>

            <form onSubmit={handleSubmit} className="mt-7" noValidate data-id="element-194" data-mp="1hrsjnaaqixb5">
              <label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint" data-id="element-195" data-mp="25gbnndmr4nxf">
                Workspace password
              </label>

              <div className="mt-2 flex items-center gap-2 rounded-xl border border-ink-700 bg-ink-850 px-3 focus-within:border-ink-500" data-id="element-196" data-mp="1vqs3m4nayo0z">
                <input id="password" name="password" type={visible ? 'text' : 'password'} value={password} autoFocus autoComplete="current-password" aria-invalid={Boolean(error)} aria-describedby={error ? 'password-error' : undefined} onChange={e => {
                setPassword(e.target.value);
                if (error) setError('');
              }} placeholder="••••••••" className="h-11 flex-1 bg-transparent text-sm text-chalk placeholder:text-faint focus:outline-none" data-id="element-197" data-mp="20oto8u0dmvek" />
                <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Hide password' : 'Show password'} className="rounded-md p-1.5 text-faint transition-colors hover:text-chalk" data-id="element-198" data-mp="3uyiat5gy25eo">
                  {visible ? <EyeOffIcon className="h-4 w-4" data-id="element-199" data-mp="3puqmsufwo8p" /> : <EyeIcon className="h-4 w-4" data-id="element-200" data-mp="1j869bw7hjsbz" />}
                </button>
              </div>

              {error && <motion.p id="password-error" role="alert" initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-2 flex items-center gap-2 text-xs text-alert" data-id="element-201" data-mp="1f747d7m6p26b">
                  <TriangleAlertIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-202" data-mp="lu4jjtqeot24" />
                  {error}
                </motion.p>}

              <button type="submit" disabled={pending || password.length === 0} className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-campaign text-sm font-medium text-ink-950 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-203" data-mp="punc5q04i35d">
                {pending && <LoaderCircleIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-204" data-mp="6xm601uu1wzu" />}
                {pending ? 'Checking…' : 'Enter dashboard'}
              </button>
            </form>

            <p className="mt-6 text-xs leading-relaxed text-faint" data-id="element-205" data-mp="3akvyepsuouxu">
              Lost the password? Ask the content lead — access is shared, there are no individual accounts.
            </p>
          </motion.div>
        </div>
      </main>
    </div>;
}